// Stamps in the drawing strip (issue 1034; design/28 §4a): naming the box drawn in Save as stamp, and the person's
// library — pick one to put it down with each click, rename it, delete it. A deleted stamp leaves its copies on the map.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { STAMP_LIMITS, stampFrom, type Stamp } from '@dragons-table/vtt-shared';
import { useTableState } from '../table/TableState';
import { drawTool, type DrawDraft } from './drawTool';
import { deleteStamp, renameStamp, saveStamp, stampLibrary, useStamps } from './stamps';
import { BTN } from '../shell/ui';

const btn = BTN;
const field = 'rounded border border-amber-500 bg-stone-900 px-1 text-stone-100 outline-none';

/** The box drawn in Save as stamp, waiting for its name: Enter saves, Escape lets it go (design/28 §4a.2). */
export function StampNameForm({ d, onSaid }: { d: DrawDraft; onSaid: (s: string) => void }) {
  const { store, meId } = useTableState();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const box = d.mode === 'stampBox' ? d.box : null;
  useLayoutEffect(() => { setName(''); if (box) ref.current?.focus(); }, [box]);
  if (!box) return null;
  const marks = stampFrom(store.state.drawings ?? [], box, meId);
  const problem = !marks.length ? 'None of your drawings is in that box. Drag again around your own marks.'
    : marks.length > STAMP_LIMITS.marks ? `A stamp keeps ${STAMP_LIMITS.marks} marks at most; that box holds ${marks.length}.` : '';
  const save = async () => {
    const n = name.trim();
    if (!n || problem || busy) return;
    setBusy(true);
    const got = await saveStamp(n.slice(0, STAMP_LIMITS.name), marks);
    setBusy(false);
    if (got.kind === 'ok') { stampLibrary.added(got.stamp); drawTool.setBox(null); onSaid(`Saved "${got.stamp.name}" to your stamps.`); return; }
    onSaid(got.kind === 'refused' ? got.reason : 'The stamp could not be saved just now.');
  };
  return (
    <span className="flex flex-wrap items-center gap-1" data-testid="draw-stamp-form">
      {problem ? <span className="text-amber-300" role="status" data-testid="draw-stamp-problem">{problem}</span> : (
        <>
          <span>{marks.length} mark{marks.length === 1 ? '' : 's'}:</span>
          <input ref={ref} className={field} data-testid="draw-stamp-name" aria-label="Stamp name" maxLength={STAMP_LIMITS.name} placeholder="Name it, then Enter" value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') { e.preventDefault(); void save(); }
              if (e.key === 'Escape') { e.preventDefault(); drawTool.setBox(null); }
            }} />
          <button type="button" className={btn} data-testid="draw-stamp-save" disabled={!name.trim() || busy} onClick={() => void save()}>Save stamp</button>
        </>
      )}
      <button type="button" className={btn} data-testid="draw-stamp-cancel" onClick={() => drawTool.setBox(null)}>Cancel</button>
    </span>
  );
}

function StampRow({ s, picked, onSaid }: { s: Stamp; picked: boolean; onSaid: (m: string) => void }) {
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(s.name);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (renaming) { setName(s.name); ref.current?.focus(); } }, [renaming, s.name]);
  const rename = async () => {
    const n = name.trim();
    if (!n || n === s.name) { setRenaming(false); return; }
    const got = await renameStamp(s.id, n.slice(0, STAMP_LIMITS.name));
    if (got.kind === 'ok') { stampLibrary.changed(got.stamp); if (picked) drawTool.pickStamp(got.stamp); setRenaming(false); return; }
    onSaid(got.kind === 'refused' ? got.reason : 'The stamp could not be renamed just now.');
  };
  const remove = async () => {
    if (!(await deleteStamp(s.id))) { onSaid('The stamp could not be deleted just now.'); return; }
    stampLibrary.removed(s.id);
    if (picked) drawTool.pickStamp(null);
    onSaid(`Deleted "${s.name}". Its copies stay on the map.`);
  };
  return (
    <li className="flex items-center gap-1" data-testid="draw-stamp" data-id={s.id} data-name={s.name}>
      {renaming ? (
        <input ref={ref} className={field} data-testid="draw-stamp-rename-input" aria-label={`New name for ${s.name}`} maxLength={STAMP_LIMITS.name} value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => setRenaming(false)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') { e.preventDefault(); void rename(); }
            if (e.key === 'Escape') { e.preventDefault(); setRenaming(false); }
          }} />
      ) : (
        <button type="button" role="radio" aria-checked={picked} aria-label={`Stamp ${s.name}`} data-testid="draw-stamp-pick"
          className={`${btn} ${picked ? 'border-amber-500 text-amber-300' : ''}`} onClick={() => drawTool.pickStamp(s)}>
          {s.name} <span className="text-stone-400">({s.marks.length})</span>
        </button>
      )}
      <button type="button" className={btn} aria-label={`Rename ${s.name}`} data-testid="draw-stamp-rename" onMouseDown={(e) => e.preventDefault()} onClick={() => setRenaming((r) => !r)}>Rename</button>
      <button type="button" className={btn} aria-label={`Delete ${s.name}`} data-testid="draw-stamp-delete" onClick={() => void remove()}>Delete</button>
    </li>
  );
}

/** The person's library (design/28 §4a.3, 5): pick one and each click on the map puts it down. */
export function StampList({ d, onSaid }: { d: DrawDraft; onSaid: (s: string) => void }) {
  const lib = useStamps();
  return (
    <div className="flex w-full flex-col gap-1" data-testid="draw-stamp-list">
      {lib.failed && <span role="status">Your stamps could not be read just now.</span>}
      {lib.loaded && !lib.failed && !lib.stamps.length && <span className="text-stone-400" data-testid="draw-stamps-empty">No stamps yet. Use Save as stamp to keep some of your drawings.</span>}
      {lib.stamps.length > 0 && (
        <ul role="radiogroup" aria-label="Your stamps" className="flex max-h-40 flex-col gap-1 overflow-y-auto">
          {lib.stamps.map((s) => <StampRow key={s.id} s={s} picked={d.mode === 'stamp' && d.stamp?.id === s.id} onSaid={onSaid} />)}
        </ul>
      )}
    </div>
  );
}
